import { useTranslate } from "ra-core";
import { lazy, Suspense } from "react";
import { Skeleton } from "@/components/ui/skeleton";

import { DashboardActivityLog } from "./DashboardActivityLog";
import { DashboardKpis } from "./DashboardKpis";
import { FirstActions } from "./FirstActions";
import { NeedsFollowUp } from "./NeedsFollowUp";
import { TodayList } from "./TodayList";
import { FocusDashboard } from "./focus/FocusDashboard";
import { isOldLayout, useDashboardPrefs } from "./focus/useDashboardPrefs";
import MobileHeader from "../layout/MobileHeader";
import { MobileContent } from "../layout/MobileContent";
import { useConfigurationContext } from "../root/ConfigurationContext";
import { useSignertUrl } from "../providers/commons/useSignertVedlegg";

const DashboardWidget = lazy(() =>
  import("./DashboardWidget").then((module) => ({
    default: module.DashboardWidget,
  })),
);

const Wrapper = ({ children }: { children: React.ReactNode }) => {
  const { darkModeLogo, lightModeLogo, title } = useConfigurationContext();
  // Opplastede logoer bor i den private vedleggsbøtta og må signeres før
  // visning; medfølgende logoer (/logos/…) går uendret gjennom.
  const darkLogoSrc = useSignertUrl(darkModeLogo);
  const lightLogoSrc = useSignertUrl(lightModeLogo);
  return (
    <>
      <MobileHeader>
        <div className="flex items-center gap-2 text-secondary-foreground no-underline py-3">
          {darkLogoSrc && (
            <img
              className="[.light_&]:hidden h-6"
              src={darkLogoSrc}
              alt={title}
            />
          )}
          {lightLogoSrc && (
            <img
              className="[.dark_&]:hidden h-6"
              src={lightLogoSrc}
              alt={title}
            />
          )}
          <h1 className="page-title">{title}</h1>
        </div>
      </MobileHeader>
      <MobileContent>{children}</MobileContent>
    </>
  );
};

// Oversikt på mobil: «Fokus» i én kolonne, eller den gamle for den som har
// valgt «Gammel oversikt».
export const MobileDashboard = () => {
  const { layout, isPending } = useDashboardPrefs();
  if (isPending) return <Wrapper>{null}</Wrapper>;
  return (
    <Wrapper>
      {isOldLayout(layout) ? <ClassicMobileDashboard /> : <FocusDashboard />}
    </Wrapper>
  );
};

// Den gamle Oversikten for mobil: nøkkeltall, forfalte oppgaver, aktive
// prosjekter og siste aktivitet.
const ClassicMobileDashboard = () => {
  const translate = useTranslate();
  return (
    <>
      {/* Samme rekkefølge som desktop, bare stablet: nøkkeltall, «I dag» og de
          to widgetene først (de tre kolonnene der), så resten – Siste
          aktivitet helt nederst. */}
      <div className="flex flex-col gap-6 mt-1">
        <FirstActions />
        <DashboardKpis />
        {/* «I dag» (V9) også på mobil — første del av mobil-løftet (V12). */}
        <TodayList />
        <Suspense fallback={<Skeleton className="h-48 w-full rounded-xl" />}>
          <DashboardWidget
            widgetId={1}
            standardNavn={translate("crm.dashboard.widget.default_1", {
              _: "Delte notater",
            })}
          />
        </Suspense>
        <Suspense fallback={<Skeleton className="h-48 w-full rounded-xl" />}>
          <DashboardWidget
            widgetId={2}
            standardNavn={translate("crm.dashboard.widget.default_2", {
              _: "Nyttige lenker",
            })}
          />
        </Suspense>
        <NeedsFollowUp />
        <DashboardActivityLog />
      </div>
    </>
  );
};
